import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../hooks/useApi';
import FloorPlanMap from './FloorPlanMap';
import { T, todayIso, fmtShort } from './bookingsTheme';
import Modal from '../shared/Modal';
import PhotoAvatar from '../shared/Avatar';

function Avatar({ name, color, size = 22, personId }) {
  const initials = name ? name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase() : '?';
  return (
    <PhotoAvatar personId={personId} style={{
      width: size, height: size, borderRadius: '50%', background: color || '#4B5563',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.38, flexShrink: 0,
    }} title={name}>{initials}</PhotoAvatar>
  );
}

const slotColor = (s) => ({ full: 'bg-blue-600', am: 'bg-amber-600', pm: 'bg-purple-600' }[s] || 'bg-gray-500');

// ─── Desks management (table + drag-to-place floor-plan) ──────────────────────

function DesksPanel() {
  const [locations, setLocations]   = useState([]);
  const [people, setPeople]         = useState([]);
  const [activeTab, setActiveTab]   = useState(0);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState({});
  const [msg, setMsg]               = useState('');
  const [newDeskLabel, setNewDeskLabel] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName]   = useState('');
  const [addingLocation, setAddingLocation] = useState(false);
  const [newLocName, setNewLocName] = useState('');
  const [newLocDesc, setNewLocDesc] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [locs, ppl] = await Promise.all([api.get('desk-locations'), api.get('people')]);
      setLocations(locs || []);
      setPeople((ppl || []).filter(p => !p.is_archived && p.status !== 'archived')
        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)));
    } catch { setMsg('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const currentLocation = locations[activeTab];

  // Optimistic, debounced-ish desk position save
  const moveDesk = async (desk, x, y) => {
    setLocations(locs => locs.map(l => l.id !== desk.location_id ? l : {
      ...l, desks: (l.desks || []).map(d => d.id === desk.id ? { ...d, pos_x: x, pos_y: y } : d),
    }));
    try { await api.put(`desks/${desk.id}`, { pos_x: x, pos_y: y }); }
    catch { flash('Failed to save desk position'); }
  };

  const parseZones = (loc) => { try { return JSON.parse(loc?.layout_json || '[]'); } catch { return []; } };

  const saveZones = async (loc, nextZones) => {
    setLocations(locs => locs.map(l => l.id === loc.id ? { ...l, layout_json: JSON.stringify(nextZones) } : l));
    try { await api.put(`desk-locations/${loc.id}`, { layout_json: JSON.stringify(nextZones) }); }
    catch { flash('Failed to save layout'); }
  };

  const addZone = (loc, type) => {
    const ratio = loc.name === 'PP' ? 0.64 : 0.55;
    const VH = Math.round(100 * ratio);
    const defaults = {
      room:  { w: 16, h: 12, label: 'Room' },
      area:  { w: 18, h: 12, label: 'Area' },
      wall:  { w: 22, h: 1.4, label: '' },
      door:  { w: 10, h: 5, label: 'Door' },
      label: { w: 16, h: 6, label: 'Label' },
    }[type];
    const zone = { id: 'z-' + Math.random().toString(36).slice(2, 8), type, x: 42, y: Math.round(VH / 2), ...defaults };
    saveZones(loc, [...parseZones(loc), zone]);
  };

  const addLocation = async () => {
    if (!newLocName.trim()) return;
    setSaving(s => ({ ...s, _newloc: true }));
    try {
      await api.post('desk-locations', { name: newLocName.trim(), description: newLocDesc.trim() || undefined });
      setNewLocName(''); setNewLocDesc(''); setAddingLocation(false);
      await load(); setActiveTab(locations.length); flash('Office added');
    } catch (e) { flash('Failed to add office: ' + (e.message || 'error')); }
    finally { setSaving(s => ({ ...s, _newloc: false })); }
  };

  const deleteLocation = async (loc) => {
    if ((loc.desks || []).length > 0) { flash(`Remove all desks from "${loc.name}" before deleting it`); return; }
    if (!window.confirm(`Delete office "${loc.name}"?`)) return;
    setSaving(s => ({ ...s, [`loc_${loc.id}`]: true }));
    try { await api.delete(`desk-locations/${loc.id}`); setActiveTab(0); await load(); flash('Office deleted'); }
    catch (e) { flash(e.message || 'Failed to delete office'); }
    finally { setSaving(s => ({ ...s, [`loc_${loc.id}`]: false })); }
  };

  const addDesk = async () => {
    if (!newDeskLabel.trim() || !currentLocation) return;
    setSaving(s => ({ ...s, _new: true }));
    try { await api.post('desks', { location_id: currentLocation.id, label: newDeskLabel.trim() }); setNewDeskLabel(''); await load(); flash('Desk added'); }
    catch (e) { flash('Failed to add desk: ' + (e.message || 'error')); }
    finally { setSaving(s => ({ ...s, _new: false })); }
  };

  const renameDesk = async (desk) => {
    const next = editName.trim();
    if (!next || next === desk.label) { setEditingId(null); return; }
    setSaving(s => ({ ...s, [desk.id]: true }));
    try { await api.put(`desks/${desk.id}`, { label: next }); await load(); setEditingId(null); flash('Desk renamed'); }
    catch { flash('Failed to rename desk'); }
    finally { setSaving(s => ({ ...s, [desk.id]: false })); }
  };

  const deleteDesk = async (desk) => {
    if (!window.confirm(`Remove desk "${desk.label}"? If it has future bookings it will be disabled instead.`)) return;
    setSaving(s => ({ ...s, [desk.id]: true }));
    try { const r = await api.delete(`desks/${desk.id}`); await load(); flash(r.disabled ? `${desk.label} disabled (has future bookings)` : `${desk.label} deleted`); }
    catch (e) { flash('Failed to remove desk: ' + (e.message || 'error')); }
    finally { setSaving(s => ({ ...s, [desk.id]: false })); }
  };

  // 3-way desk state: bookable / reserved (shown for info) / blocked (out of service).
  const setDeskStatus = async (desk, status) => {
    if (desk.status === status || (!desk.status && status === (desk.is_active ? 'bookable' : 'blocked'))) return;
    setSaving(s => ({ ...s, [desk.id]: true }));
    try { await api.put(`desks/${desk.id}`, { status }); await load(); }
    catch { flash('Failed to update desk status'); }
    finally { setSaving(s => ({ ...s, [desk.id]: false })); }
  };

  const saveStatusNote = async (deskId, note) => {
    setSaving(s => ({ ...s, [deskId]: true }));
    try { await api.put(`desks/${deskId}`, { status_note: note || null }); await load(); }
    catch { flash('Failed to save reason'); }
    finally { setSaving(s => ({ ...s, [deskId]: false })); }
  };

  const assignPerson = async (deskId, personId) => {
    setSaving(s => ({ ...s, [deskId]: true }));
    await api.put(`desks/${deskId}`, { assigned_person_id: personId || null });
    await load(); setSaving(s => ({ ...s, [deskId]: false }));
  };

  const setAutoRelease = async (desk, value) => {
    setSaving(s => ({ ...s, [desk.id]: true }));
    await api.put(`desks/${desk.id}`, { auto_release_on_leave: value }).catch(() => {});
    setSaving(s => ({ ...s, [desk.id]: false }));
    flash(value ? `${desk.label} will free up while its owner is on leave` : `${desk.label} stays locked`);
    load();
  };

  const saveNotes = async (deskId, notes) => {
    setSaving(s => ({ ...s, [deskId]: true }));
    await api.put(`desks/${deskId}`, { notes: notes || null });
    await load(); setSaving(s => ({ ...s, [deskId]: false }));
  };

  if (loading) return <div className="p-4 text-[10px] text-gray-400">Loading…</div>;

  return (
    <div className="space-y-4">
      {msg && <div className="px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded text-[10px]">{msg}</div>}

      {/* Office tabs */}
      <div className="flex flex-wrap gap-0 border-b border-gray-200 items-end">
        {locations.map((loc, i) => (
          <button key={loc.id} onClick={() => setActiveTab(i)}
            className={`px-3 py-1.5 text-[10px] font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === i ? 'border-navy text-navy' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {loc.name}<span className="ml-1 font-normal text-gray-400">({(loc.desks || []).filter(d => d.is_active).length})</span>
          </button>
        ))}
        <button onClick={() => setAddingLocation(v => !v)}
          className="mb-px ml-1 px-2 py-1 text-[10px] font-semibold text-navy border border-navy/30 rounded hover:bg-navy/5 transition-colors">+ Office</button>
      </div>

      {addingLocation && (
        <div className="bg-gray-50 border border-gray-200 rounded p-3 flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-36">
            <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Office name</label>
            <input type="text" value={newLocName} onChange={e => setNewLocName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addLocation()}
              placeholder="e.g. PP" className="w-full border border-gray-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy" autoFocus />
          </div>
          <div className="flex-1 min-w-36">
            <label className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Description (optional)</label>
            <input type="text" value={newLocDesc} onChange={e => setNewLocDesc(e.target.value)} onKeyDown={e => e.key === 'Enter' && addLocation()}
              placeholder="e.g. Pride Park office" className="w-full border border-gray-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy" />
          </div>
          <div className="flex gap-2">
            <button onClick={addLocation} disabled={!newLocName.trim() || !!saving._newloc}
              className="px-3 py-1 bg-navy text-white rounded text-[10px] font-semibold hover:bg-navy/90 disabled:opacity-40">{saving._newloc ? 'Adding…' : 'Add'}</button>
            <button onClick={() => { setAddingLocation(false); setNewLocName(''); setNewLocDesc(''); }}
              className="px-3 py-1 border border-gray-200 rounded text-[10px] text-gray-600 hover:bg-gray-100">Cancel</button>
          </div>
        </div>
      )}

      {currentLocation && (
        <div className="space-y-4">
          {/* Floor-plan editor */}
          <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-3 py-2 bg-gradient-to-r from-navy to-[#27496b] text-white text-[10px] font-bold flex items-center justify-between">
              <span className="flex items-center gap-1.5">🗺 Floor Plan — {currentLocation.name}</span>
              <button onClick={() => deleteLocation(currentLocation)} disabled={!!saving[`loc_${currentLocation.id}`]}
                className="text-red-200 hover:text-white hover:bg-red-500/30 px-2 py-0.5 rounded text-[9px] font-medium disabled:opacity-40 transition-colors">Delete office</button>
            </div>
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-2 flex-wrap">
              <span className="text-[8px] font-bold text-gray-400 uppercase tracking-wide mr-1">Add to plan</span>
              <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden bg-white">
                {[['room', '🏠 Room'], ['area', '▢ Area'], ['wall', '▬ Wall'], ['door', '🚪 Door'], ['label', '🏷 Label']].map(([t, lbl]) => (
                  <button key={t} onClick={() => addZone(currentLocation, t)}
                    className="px-2.5 py-1 border-r last:border-r-0 border-gray-200 text-[9px] font-semibold text-gray-600 hover:bg-navy hover:text-white transition-colors">{lbl}</button>
                ))}
              </div>
              <span className="text-[8px] text-gray-400 ml-auto">Drag to move · drag the corner to resize · double-click a room to rename</span>
            </div>
            <div className="p-3">
              <FloorPlanMap
                desks={currentLocation.desks || []}
                editable
                onMove={moveDesk}
                zones={parseZones(currentLocation)}
                onZonesChange={(z) => saveZones(currentLocation, z)}
                getState={(d) => ({ status: d.status === 'reserved' ? 'reserved' : (d.is_active ? 'available' : 'blocked'), note: d.status_note })}
                heightRatio={currentLocation.name === 'PP' ? 0.64 : 0.55}
              />
              <div className="mt-1 text-[8px] text-gray-400">Amber = reserved (shown for info, not bookable) · grey/hatched = blocked out. Set each desk's state in the table below.</div>
            </div>
          </div>

          {/* Desk list */}
          <div className="border border-gray-200 rounded overflow-hidden">
            <div className="px-3 py-2 bg-navy text-white text-[10px] font-bold flex items-center justify-between">
              <span>Desks — {currentLocation.name}</span>
              <span className="text-gray-300 font-normal">{(currentLocation.desks || []).filter(d => d.is_active).length} of {(currentLocation.desks || []).length} bookable</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-3 py-1.5 text-left font-semibold text-gray-500 w-20">Desk</th>
                    <th className="px-3 py-1.5 text-left font-semibold text-gray-500 w-28">Status</th>
                    <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Permanent seat</th>
                    <th className="px-3 py-1.5 text-left font-semibold text-gray-500 w-32">Free when away</th>
                    <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Features / Notes</th>
                    <th className="px-2 py-1.5 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(currentLocation.desks || []).length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400 text-[10px]">No desks configured</td></tr>
                  )}
                  {(currentLocation.desks || []).map(desk => {
                      const deskStatus = desk.status || (desk.is_active ? 'bookable' : 'blocked');
                      const rowTint = deskStatus === 'reserved' ? 'bg-amber-50/40' : !desk.is_active ? 'opacity-70 bg-gray-50' : 'hover:bg-gray-50/50';
                      const STATUS_OPTS = [
                        { key: 'bookable', label: 'Bookable', on: 'bg-green-600 text-white border-green-600', off: 'text-green-700 hover:bg-green-50' },
                        { key: 'reserved', label: 'Reserved', on: 'bg-amber-500 text-amber-950 border-amber-500', off: 'text-amber-700 hover:bg-amber-50' },
                        { key: 'blocked',  label: 'Blocked',  on: 'bg-gray-500 text-white border-gray-500', off: 'text-gray-500 hover:bg-gray-100' },
                      ];
                      return (
                    <tr key={desk.id} className={rowTint}>
                      <td className="px-3 py-2 font-mono font-semibold text-gray-700 align-top">
                        {editingId === desk.id ? (
                          <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') renameDesk(desk); if (e.key === 'Escape') setEditingId(null); }}
                            onBlur={() => renameDesk(desk)} disabled={!!saving[desk.id]}
                            className="border border-gray-300 rounded px-1.5 py-0.5 text-[10px] font-mono w-20 focus:outline-none focus:ring-1 focus:ring-navy" autoFocus />
                        ) : (
                          <span onClick={() => { setEditingId(desk.id); setEditName(desk.label); }} className="cursor-pointer hover:text-navy" title="Click to rename">{desk.label}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                          {STATUS_OPTS.map(o => (
                            <button key={o.key} onClick={() => setDeskStatus(desk, o.key)} disabled={!!saving[desk.id]}
                              className={`px-2 py-1 text-[9px] font-semibold border-r last:border-r-0 border-gray-200 transition-colors disabled:opacity-50 ${deskStatus === o.key ? o.on : `bg-white ${o.off}`}`}>
                              {o.label}
                            </button>
                          ))}
                        </div>
                        {deskStatus === 'reserved' && (
                          <input type="text" defaultValue={desk.status_note || ''}
                            onBlur={e => { const v = e.target.value.trim(); if (v !== (desk.status_note || '').trim()) saveStatusNote(desk.id, v); }}
                            disabled={!!saving[desk.id]} placeholder="Reason (e.g. Director's desk)"
                            className="mt-1 block w-full max-w-[200px] border border-amber-200 bg-amber-50/50 rounded px-2 py-1 text-[9px] focus:outline-none focus:ring-1 focus:ring-amber-400" />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {desk.assigned_person_name && <Avatar name={desk.assigned_person_name} color={desk.assigned_person_color} personId={desk.assigned_person_id} />}
                          <select value={desk.assigned_person_id || ''} onChange={e => assignPerson(desk.id, e.target.value)} disabled={!!saving[desk.id]}
                            className="border border-gray-200 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy bg-white disabled:opacity-50 max-w-[220px]">
                            <option value="">— Hot desk —</option>
                            {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.job_title ? ` — ${p.job_title}` : ''}</option>)}
                          </select>
                        </div>
                      </td>
                      {/* A reserved desk opens to everybody else while its owner
                          cannot be at it: on leave, having said they are somewhere
                          else, or having booked a different seat. On by default;
                          switch it off for a seat that must stay theirs no matter
                          what, which beats all three. */}
                      <td className="px-3 py-2">
                        {desk.assigned_person_id && desk.status === 'reserved' ? (
                          <button
                            onClick={() => setAutoRelease(desk, desk.auto_release_on_leave ? 0 : 1)}
                            disabled={!!saving[desk.id]}
                            className={`px-2 py-1 rounded border text-[10px] font-semibold disabled:opacity-50 ${
                              desk.auto_release_on_leave
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                                : 'bg-gray-50 border-gray-300 text-gray-600'
                            }`}
                            title="Release this desk for others to book on days its owner is on leave, has said they are elsewhere, or has booked another desk"
                          >
                            {desk.auto_release_on_leave ? 'Releases when away' : 'Always locked'}
                          </button>
                        ) : desk.status === 'reserved' ? (
                          <span className="text-[10px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5"
                            title="A reserved desk frees itself when its owner is away. With nobody assigned there is no owner, so it never frees and nobody can book it.">
                            No owner — nobody can book this
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-400">
                            {desk.assigned_person_id ? 'Set status to Reserved' : '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <input type="text" defaultValue={desk.notes || ''}
                          onBlur={e => { const v = e.target.value.trim(); if (v !== (desk.notes || '').trim()) saveNotes(desk.id, v); }}
                          disabled={!!saving[desk.id]} placeholder="e.g. standing desk, dual monitors"
                          className="w-full border border-gray-200 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy bg-white disabled:opacity-50 min-w-[160px]" />
                      </td>
                      <td className="px-2 py-2 text-right">
                        {saving[desk.id] ? <span className="text-[9px] text-gray-400">…</span> : (
                          <button onClick={() => deleteDesk(desk)} className="text-gray-300 hover:text-red-500 transition-colors text-base leading-none font-bold" title="Remove desk">×</button>
                        )}
                      </td>
                    </tr>
                      );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 flex items-center gap-2">
              <input type="text" value={newDeskLabel} onChange={e => setNewDeskLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && addDesk()}
                placeholder={`e.g. ${currentLocation.name}-${(currentLocation.desks || []).length + 1}`}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy" />
              <button onClick={addDesk} disabled={!newDeskLabel.trim() || !!saving._new}
                className="px-3 py-1 bg-navy text-white rounded text-[10px] font-semibold hover:bg-navy/90 disabled:opacity-40">{saving._new ? 'Adding…' : '+ Add Desk'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Vans management ──────────────────────────────────────────────────────────

function VansPanel() {
  const [vans, setVans]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState({});
  const [msg, setMsg]           = useState('');
  const [newVanName, setNewVanName] = useState('');
  const [newVanDesc, setNewVanDesc] = useState('');
  const [editingId, setEditingId]   = useState(null);
  const [editName, setEditName]     = useState('');
  // Taking a van out of service: { van, action: 'disable' | 'remove', upcoming }.
  // upcoming is null while its bookings are being counted.
  const [pending, setPending]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setVans(await api.get('bookable-resources', { type: 'van' }) || []); }
    catch { setMsg('Failed to load vans'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const addVan = async () => {
    if (!newVanName.trim()) return;
    setSaving(s => ({ ...s, _new: true }));
    try { await api.post('bookable-resources', { resource_type: 'van', name: newVanName.trim(), description: newVanDesc.trim() || undefined }); setNewVanName(''); setNewVanDesc(''); await load(); flash('Vehicle added'); }
    catch (e) { flash('Failed to add vehicle: ' + (e.message || 'error')); }
    finally { setSaving(s => ({ ...s, _new: false })); }
  };
  const setActive = async (van, active) => {
    setSaving(s => ({ ...s, [van.id]: true }));
    try {
      await api.put(`bookable-resources/${van.id}`, { is_active: active ? 1 : 0 });
      await load();
      if (!active) flash(`${van.name} disabled`);
    }
    catch { flash('Failed to update vehicle'); }
    finally { setSaving(s => ({ ...s, [van.id]: false })); }
  };

  // A disabled van disappears from everybody's Vans tab except admin and HR, and
  // its upcoming bookings stay put. That used to happen on one click of the
  // badge, and Remove did it silently whenever the van had bookings, so people
  // were left looking at "No vans available" with nobody knowing why. Count the
  // bookings first and say what will actually happen before anything changes.
  const askToTakeOut = async (van, action) => {
    setPending({ van, action, upcoming: null });
    let upcoming;
    try { upcoming = await api.get('bookings', { resource_id: van.id, from: todayIso(), to: '9999-12-31' }) || []; }
    catch { upcoming = undefined; }
    setPending(p => (p?.van.id === van.id ? { ...p, upcoming } : p));
  };
  const takeOut = async () => {
    const { van, action, upcoming } = pending;
    setPending(null);
    if (action === 'disable' || upcoming?.length) return setActive(van, false);
    setSaving(s => ({ ...s, [van.id]: true }));
    try { const r = await api.delete(`bookable-resources/${van.id}`); await load(); flash(r.disabled ? `${van.name} disabled (it has upcoming bookings)` : `${van.name} deleted`); }
    catch { flash('Failed to remove vehicle'); }
    finally { setSaving(s => ({ ...s, [van.id]: false })); }
  };
  const saveRename = async (van) => {
    if (!editName.trim() || editName === van.name) { setEditingId(null); return; }
    setSaving(s => ({ ...s, [van.id]: true }));
    try { await api.put(`bookable-resources/${van.id}`, { name: editName.trim() }); await load(); setEditingId(null); flash('Vehicle renamed'); }
    catch { flash('Failed to rename vehicle'); }
    finally { setSaving(s => ({ ...s, [van.id]: false })); }
  };

  if (loading) return <div className="p-4 text-[10px] text-gray-400">Loading…</div>;

  return (
    <div className="space-y-3">
      {msg && <div className="px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded text-[10px]">{msg}</div>}
      <div className="border border-gray-200 rounded overflow-hidden">
        <div className="px-3 py-2 bg-navy text-white text-[10px] font-bold flex items-center justify-between">
          <span>Vehicles</span>
          <span className="text-gray-300 font-normal">{vans.filter(v => v.is_active).length} of {vans.length} bookable</span>
        </div>
        <table className="w-full text-[10px]">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Name</th>
              <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Description</th>
              <th className="px-3 py-1.5 text-left font-semibold text-gray-500 w-24">Status</th>
              <th className="px-2 py-1.5 w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {vans.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400 text-[10px]">No vehicles configured</td></tr>}
            {vans.map(van => (
              <tr key={van.id} className={`${!van.is_active ? 'opacity-50 bg-gray-50' : 'hover:bg-gray-50/50'}`}>
                <td className="px-3 py-2 font-semibold text-gray-700">
                  {editingId === van.id ? (
                    <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveRename(van); if (e.key === 'Escape') setEditingId(null); }}
                      onBlur={() => saveRename(van)} className="border border-gray-300 rounded px-2 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy w-32" autoFocus />
                  ) : (
                    <span onClick={() => { setEditingId(van.id); setEditName(van.name); }} className="cursor-pointer hover:text-navy" title="Click to rename">{van.name}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-400">{van.description || '—'}</td>
                <td className="px-3 py-2">
                  <button onClick={() => !saving[van.id] && (van.is_active ? askToTakeOut(van, 'disable') : setActive(van, true))} disabled={!!saving[van.id]}
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[9px] font-semibold border transition-colors disabled:opacity-50 ${
                      van.is_active ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' : 'bg-gray-100 border-gray-200 text-gray-500 hover:bg-gray-200'
                    }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${van.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                    {van.is_active ? 'Bookable' : 'Disabled'}
                  </button>
                </td>
                <td className="px-2 py-2 text-right">
                  {saving[van.id] ? <span className="text-[9px] text-gray-400">…</span> : (
                    <button onClick={() => askToTakeOut(van, 'remove')} className="text-gray-300 hover:text-red-500 transition-colors text-base leading-none font-bold" title="Remove vehicle">×</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 flex flex-wrap items-center gap-2">
          <input type="text" value={newVanName} onChange={e => setNewVanName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addVan()}
            placeholder="Vehicle name (e.g. Van 4)" className="flex-1 min-w-28 border border-gray-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy" />
          <input type="text" value={newVanDesc} onChange={e => setNewVanDesc(e.target.value)} onKeyDown={e => e.key === 'Enter' && addVan()}
            placeholder="Description (optional)" className="flex-1 min-w-28 border border-gray-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy" />
          <button onClick={addVan} disabled={!newVanName.trim() || !!saving._new}
            className="px-3 py-1 bg-navy text-white rounded text-[10px] font-semibold hover:bg-navy/90 disabled:opacity-40">{saving._new ? 'Adding…' : '+ Add Vehicle'}</button>
        </div>
      </div>
      {pending && <TakeVanOutDialog {...pending} onConfirm={takeOut} onClose={() => setPending(null)} />}
    </div>
  );
}

// What disabling or removing a van will do, with its upcoming bookings named.
// A van with bookings cannot be deleted, so Remove on one offers to disable it
// rather than asking to delete and disabling behind the person's back.
function TakeVanOutDialog({ van, action, upcoming, onConfirm, onClose }) {
  const counting = upcoming === null;
  const unknown  = upcoming === undefined;
  const booked   = upcoming?.length || 0;
  // Already disabled and still booked: there is nothing this dialog can do.
  const stuck    = action === 'remove' && booked > 0 && !van.is_active;
  const disabling = action === 'disable' || booked > 0;
  const title = action === 'remove'
    ? (booked ? `${van.name} can't be deleted` : `Delete ${van.name}?`)
    : `Disable ${van.name}?`;
  const plural = booked === 1 ? 'booking' : 'bookings';
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={stuck ? (
        <button onClick={onClose} className="px-3 py-1.5 border border-gray-300 rounded text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50">Close</button>
      ) : (
        <>
          <button onClick={onClose} className="px-3 py-1.5 border border-gray-300 rounded text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50">
            {disabling ? 'Keep it bookable' : `Keep ${van.name}`}
          </button>
          <button onClick={onConfirm} disabled={counting}
            className={`px-3 py-1.5 rounded text-xs font-semibold text-white disabled:opacity-40 ${disabling ? 'bg-navy hover:bg-navy/90' : 'bg-red-600 hover:bg-red-700'}`}>
            {counting ? 'Checking bookings…' : action === 'remove' && booked ? 'Disable instead' : disabling ? 'Disable van' : 'Delete van'}
          </button>
        </>
      )}
    >
      <div className="space-y-2 text-xs text-gray-700">
        {stuck ? (
          <p>It is already disabled, and it has {booked} upcoming {plural}. It can be deleted once they have passed or been cancelled.</p>
        ) : action === 'remove' && booked > 0 && (
          <p>It has {booked} upcoming {plural}, so it can only be disabled.</p>
        )}
        {stuck ? null : disabling ? (
          <p>Nobody will be able to book it, and it will disappear from the Vans tab for everyone except admin and HR.</p>
        ) : (
          <p>It has no upcoming bookings. Deleting it cannot be undone.</p>
        )}
        {unknown && (
          <p className="text-amber-700">Its bookings could not be checked. If it has any upcoming, it will be disabled rather than deleted, and they will stay booked.</p>
        )}
        {booked > 0 && (
          <>
            {!stuck && <p>{booked === 1 ? 'That booking stays' : `All ${booked} stay`} in place, so let the people booked know:</p>}
            <ul className="rounded border border-gray-200 divide-y divide-gray-100">
              {upcoming.slice(0, 8).map(b => (
                <li key={b.id} className="px-2.5 py-1.5">
                  <span className="font-semibold text-gray-800">{fmtShort(b.booking_date)}</span>
                  {' '}{b.person_name}{b.notes ? ` (${b.notes})` : ''}
                </li>
              ))}
              {booked > 8 && <li className="px-2.5 py-1.5 text-gray-500">and {booked - 8} more</li>}
            </ul>
          </>
        )}
        {disabling && action === 'disable' && booked === 0 && !counting && !unknown && (
          <p>It has no upcoming bookings.</p>
        )}
      </div>
    </Modal>
  );
}

// ─── Overview (who has booked what, this week) ────────────────────────────────

// ─── Overview: who is booked over the next fortnight ─────────────────────────
function OverviewPanel() {
  const [bookings, setBookings] = useState([]);
  const [grid, setGrid]         = useState(null);
  const [loading, setLoading]   = useState(true);

  const days = useMemo(() => {
    const out = []; let d = new Date(); d.setHours(12, 0, 0, 0);
    while (out.length < 10) { const wd = d.getDay(); if (wd !== 0 && wd !== 6) out.push(d.toISOString().slice(0, 10)); d = new Date(d.getTime() + 86400000); }
    return out;
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [bks, wa] = await Promise.all([
        api.get('bookings', { from: days[0], to: days[days.length - 1] }).catch(() => []),
        api.get('whereabouts', { from: days[0], to: days[days.length - 1] }).catch(() => null),
      ]);
      setBookings(bks || []); setGrid(wa); setLoading(false);
    })();
  }, [days]);

  if (loading) return <div className={`${T.body} text-gray-500 p-6 text-center`}>Loading…</div>;

  const dayLabel = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
  const nameOf = (id) => grid?.people?.find(p => p.id === id)?.name || 'Someone';

  return (
    <div className="space-y-3">
      {days.map(date => {
        const desks = bookings.filter(b => b.resource_type === 'desk' && b.booking_date === date);
        const vans  = bookings.filter(b => b.resource_type === 'van'  && b.booking_date === date);
        // Anyone who stated a non-office day. Desk bookings are already listed
        // above, so showing them again here would double-count the day.
        const away = Object.entries(grid?.days?.[date] || {})
          .filter(([, w]) => w?.source === 'explicit' && w.code !== 'office');
        if (!desks.length && !vans.length && !away.length) return null;
        return (
          <div key={date} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <span className={`${T.label} text-gray-600`}>{dayLabel(date)}</span>
              <span className={`${T.meta} text-gray-500`}>{desks.length} in the office</span>
            </div>
            <div className="p-2.5 flex flex-wrap gap-1.5">
              {desks.map(b => (
                <span key={b.id} className={`inline-flex items-center gap-1.5 ${T.meta} bg-blue-50 border border-blue-200 text-blue-800 rounded-full px-2.5 py-1`}>
                  <span className="font-semibold">{b.resource_name}</span>
                  {b.guest_name || b.person_name}
                  {b.slot !== 'full' && <span className="uppercase opacity-70">{b.slot}</span>}
                </span>
              ))}
              {vans.map(b => (
                <span key={b.id} className={`inline-flex items-center gap-1.5 ${T.meta} bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-2.5 py-1`}>
                  🚐 <span className="font-semibold">{b.resource_name}</span> {b.person_name}
                </span>
              ))}
              {away.map(([pid, w]) => (
                <span key={pid} className={`inline-flex items-center gap-1.5 ${T.meta} bg-gray-100 border border-gray-200 text-gray-700 rounded-full px-2.5 py-1`}>
                  {w.icon} {nameOf(pid)}
                </span>
              ))}
            </div>
          </div>
        );
      })}
      {!bookings.length && <div className={`${T.body} text-gray-500 p-8 text-center`}>No bookings in the next two weeks</div>}
    </div>
  );
}

// ─── Office policy ───────────────────────────────────────────────────────────
// The levers an office admin actually reaches for. Before this, capacity could
// only be changed by switching desks off one at a time, and the booking window
// and weekend rules were hardcoded.
function PolicyPanel() {
  const [locs, setLocs] = useState([]);
  const [msg, setMsg]   = useState('');

  const load = useCallback(async () => setLocs(await api.get('desk-locations').catch(() => [])), []);
  useEffect(() => { load(); }, [load]);

  const save = async (loc, patch) => {
    await api.put(`desk-locations/${loc.id}`, patch).catch(() => {});
    setMsg(`${loc.name} updated`);
    setTimeout(() => setMsg(''), 2500);
    load();
  };

  // Empty means "no limit", which is a different thing from zero.
  const numOrNull = (v) => (v === '' || v == null ? null : Number(v));

  return (
    <div className="space-y-3">
      {msg && <div className={`${T.body} bg-emerald-50 border border-emerald-300 text-emerald-900 rounded-lg px-3 py-2`}>{msg}</div>}
      {locs.map(loc => (
        <div key={loc.id} className="bg-white rounded-lg border border-gray-200 p-3">
          <div className={`${T.title} text-gray-800 mb-3`}>{loc.name}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className={`${T.label} text-gray-500 block mb-1`}>Book ahead (days)</span>
              <input
                type="number" min="0" defaultValue={loc.booking_window_days ?? ''}
                onBlur={e => save(loc, { booking_window_days: numOrNull(e.target.value) })}
                placeholder="No limit"
                className={`${T.body} w-full border border-gray-300 rounded-md px-2.5 py-2`}
              />
              <span className={`${T.meta} text-gray-500`}>Blank for no limit</span>
            </label>
            <label className="block">
              <span className={`${T.label} text-gray-500 block mb-1`}>Daily capacity</span>
              <input
                type="number" min="0" defaultValue={loc.capacity ?? ''}
                onBlur={e => save(loc, { capacity: numOrNull(e.target.value) })}
                placeholder="No limit"
                className={`${T.body} w-full border border-gray-300 rounded-md px-2.5 py-2`}
              />
              <span className={`${T.meta} text-gray-500`}>People per day, whatever the desk count</span>
            </label>
            <label className="block">
              <span className={`${T.label} text-gray-500 block mb-1`}>Weekends</span>
              <button
                type="button"
                onClick={() => save(loc, { allow_weekends: loc.allow_weekends ? 0 : 1 })}
                className={`${T.body} w-full border rounded-md px-2.5 py-2 font-semibold ${
                  loc.allow_weekends ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-gray-300 text-gray-600'
                }`}
              >
                {loc.allow_weekends ? 'Open at weekends' : 'Weekdays only'}
              </button>
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Entrance displays ───────────────────────────────────────────────────────
// The tablets at the office doors showing the floor plan (/display). Each one
// is a device with a username and password of its own, created here. The
// password is shown exactly once, at creation or reset, because it is never
// stored in the clear: whoever is setting the tablet up types it in there and
// then. The tablet stays signed in for 30 days, or until it has gone four days
// without refreshing (switched off), and then asks for the password again.
const fmtWhen = (iso) => iso
  ? new Date(iso.replace(' ', 'T') + 'Z').toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  : null;

function DisplayDevicesPanel() {
  const [devices, setDevices] = useState([]);
  const [locs, setLocs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState('');
  const [form, setForm]       = useState({ name: '', username: '', location_id: '' });
  // The one-time password, with the device it belongs to, shown until dismissed.
  const [reveal, setReveal]   = useState(null);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const load = useCallback(async () => {
    const [d, l] = await Promise.all([
      api.get('display-devices').catch(() => []),
      api.get('desk-locations').catch(() => []),
    ]);
    setDevices(d || []); setLocs((l || []).filter(x => x.desk_required !== 0));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); await load(); if (ok) flash(ok); return r; }
    catch (e) { flash(e.data?.error || e.message || 'That did not work'); return null; }
    finally { setBusy(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    const r = await run(() => api.post('display-devices', { ...form, location_id: form.location_id || null }));
    if (r?.password) {
      setReveal({ device: r.device, password: r.password, why: 'created' });
      setForm({ name: '', username: '', location_id: '' });
    }
  };
  const reset = async (d) => {
    if (!window.confirm(`Reset the password for "${d.name}"? The tablet will be signed out and need the new one.`)) return;
    const r = await run(() => api.post(`display-devices/${d.id}/reset-password`, {}));
    if (r?.password) setReveal({ device: d, password: r.password, why: 'reset' });
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.name}"? It will be signed out and cannot sign in again.`)) return;
    await run(() => api.delete(`display-devices/${d.id}`), 'Display removed');
  };

  const displayUrl = `${window.location.origin}/display`;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-3">
        <p className={`${T.meta} text-gray-500 max-w-xl`}>
          A tablet at the office entrance opens <span className="font-semibold text-gray-700">{displayUrl}</span> and
          signs in with a username and password from here. It then shows the floor plan and who is at which desk
          today, and nothing else. A tablet stays signed in for 30 days, or until it has been switched off for four
          days, and then asks for the password again. Reset the password to sign a tablet out from here.
        </p>
        {msg && <span className={`${T.meta} font-semibold text-navy`}>{msg}</span>}
      </div>

      {reveal && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 mb-4">
          <div className={`${T.strong} text-amber-900`}>
            {reveal.why === 'created' ? 'Display created.' : 'Password reset.'} Type this into the tablet now: it is shown once.
          </div>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mt-2">
            <span className={`${T.body} text-amber-900`}>Address <span className="font-mono font-semibold">{displayUrl}</span></span>
            <span className={`${T.body} text-amber-900`}>Username <span className="font-mono font-semibold">{reveal.device.username}</span></span>
            <span className={`${T.body} text-amber-900`}>Password <span className="font-mono font-semibold tracking-wide select-all">{reveal.password}</span></span>
          </div>
          <button type="button" onClick={() => setReveal(null)} className={`${T.meta} text-amber-800 underline mt-2`}>
            I have typed it in
          </button>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden mb-4">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {['Display', 'Username', 'Office', 'Last signed in', 'Status', ''].map(h => (
                <th key={h} className={`${T.label} text-gray-500 text-left px-3 py-2`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && <tr><td colSpan={6} className={`${T.body} text-gray-500 px-3 py-4`}>Loading…</td></tr>}
            {!loading && devices.length === 0 && (
              <tr><td colSpan={6} className={`${T.body} text-gray-500 px-3 py-4`}>No displays yet. Add one below.</td></tr>
            )}
            {devices.map(d => (
              <tr key={d.id}>
                <td className={`${T.body} font-semibold text-gray-900 px-3 py-2`}>{d.name}</td>
                <td className={`${T.body} font-mono text-gray-700 px-3 py-2`}>{d.username}</td>
                <td className={`${T.body} text-gray-700 px-3 py-2`}>{d.location_name || 'Every office'}</td>
                <td className={`${T.body} text-gray-500 px-3 py-2`}>{fmtWhen(d.last_login) || 'Never'}</td>
                <td className={`${T.body} px-3 py-2`}>
                  {d.locked_at ? <span className="text-red-700 font-semibold">Blocked</span>
                    : d.is_active ? <span className="text-emerald-700 font-semibold">Active</span>
                    : <span className="text-gray-500">Switched off</span>}
                </td>
                <td className={`${T.meta} px-3 py-2 text-right whitespace-nowrap`}>
                  {d.locked_at && (
                    <button type="button" disabled={busy} onClick={() => run(() => api.post(`display-devices/${d.id}/unlock`, {}), 'Display unlocked')}
                      className="text-navy font-semibold underline mr-3">Unlock</button>
                  )}
                  <button type="button" disabled={busy} onClick={() => reset(d)} className="text-navy font-semibold underline mr-3">Reset password</button>
                  <button type="button" disabled={busy}
                    onClick={() => run(() => api.put(`display-devices/${d.id}/active`, { is_active: !d.is_active }), d.is_active ? 'Display switched off' : 'Display switched on')}
                    className="text-gray-600 underline mr-3">{d.is_active ? 'Switch off' : 'Switch on'}</button>
                  <button type="button" disabled={busy} onClick={() => remove(d)} className="text-red-600 underline">Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={create} className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className={`${T.label} text-gray-500 block mb-1`}>Display name</span>
          <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Pride Park entrance" className={`${T.body} border border-gray-300 rounded-md px-2.5 py-1.5 w-56`} />
        </label>
        <label className="block">
          <span className={`${T.label} text-gray-500 block mb-1`}>Username</span>
          <input required value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
            placeholder="pp-entrance" autoCapitalize="none" className={`${T.body} border border-gray-300 rounded-md px-2.5 py-1.5 w-40 font-mono`} />
        </label>
        <label className="block">
          <span className={`${T.label} text-gray-500 block mb-1`}>Office</span>
          <select value={form.location_id} onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))}
            className={`${T.body} border border-gray-300 rounded-md px-2.5 py-1.5`}>
            <option value="">Every office</option>
            {locs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        <button type="submit" disabled={busy} className={`${T.body} font-semibold text-white rounded-md px-4 py-2 disabled:opacity-60`}
          style={{ background: 'var(--navy, #0D1D32)' }}>
          Add display
        </button>
      </form>
    </div>
  );
}

// ─── Utilisation report ──────────────────────────────────────────────────────
// The commercial question the module could not previously answer: are we
// carrying more desks than we use?
function ReportPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 27 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo]     = useState(today);
  const [locId, setLocId] = useState('');
  const [locs, setLocs] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('desk-locations').then(setLocs).catch(() => {}); }, []);

  const run = useCallback(async () => {
    setLoading(true);
    setData(await api.get('booking-reports', { from, to, ...(locId ? { location_id: locId } : {}) }).catch(() => null));
    setLoading(false);
  }, [from, to, locId]);

  useEffect(() => { run(); }, [run]);

  const csvHref = `/api/booking-reports?${new URLSearchParams({ from, to, format: 'csv', ...(locId ? { location_id: locId } : {}) })}`;
  const maxPeople = Math.max(1, ...(data?.by_day || []).map(d => d.people || 0));

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-lg border border-gray-200 p-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className={`${T.label} text-gray-500 block mb-1`}>From</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className={`${T.body} border border-gray-300 rounded-md px-2.5 py-2`} />
        </label>
        <label className="block">
          <span className={`${T.label} text-gray-500 block mb-1`}>To</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className={`${T.body} border border-gray-300 rounded-md px-2.5 py-2`} />
        </label>
        <label className="block">
          <span className={`${T.label} text-gray-500 block mb-1`}>Office</span>
          <select value={locId} onChange={e => setLocId(e.target.value)}
            className={`${T.body} border border-gray-300 rounded-md px-2.5 py-2`}>
            <option value="">All offices</option>
            {locs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        <a href={csvHref} download
          className={`${T.body} font-semibold px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 ml-auto`}>
          Download CSV
        </a>
      </div>

      {loading && <div className={`${T.body} text-gray-500 p-6 text-center`}>Working it out…</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'Utilisation', value: data.utilisation_pct != null ? `${data.utilisation_pct}%` : '—',
                note: `of ${data.bookable_desks} desks over ${data.working_days} working days` },
              { label: 'Busiest day', value: data.busiest_day ? `${data.busiest_day.people}` : '—',
                note: data.busiest_day ? new Date(data.busiest_day.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' }) : '' },
              { label: 'Quietest weekday', value: data.quietest_day ? `${data.quietest_day.people}` : '—',
                note: data.quietest_day ? new Date(data.quietest_day.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' }) : '' },
              { label: 'Never booked', value: `${data.never_booked?.length || 0}`,
                note: data.never_booked?.length ? data.never_booked.slice(0, 6).join(', ') : 'Every desk used at least once' },
            ].map(t => (
              <div key={t.label} className="bg-white rounded-lg border border-gray-200 p-3">
                <div className={`${T.label} text-gray-500`}>{t.label}</div>
                <div className={`${T.figure} text-navy mt-1`}>{t.value}</div>
                <div className={`${T.meta} text-gray-500 mt-0.5`}>{t.note}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className={`${T.label} text-gray-500 mb-2`}>People in, by day</div>
            <div className="flex items-end gap-1 h-32 overflow-x-auto">
              {(data.by_day || []).map(d => (
                <div key={d.date} className="flex flex-col items-center justify-end flex-1 min-w-[16px]" title={`${d.date}: ${d.people} in`}>
                  <span className={`${T.meta} text-gray-500`}>{d.people}</span>
                  <div className="w-full bg-navy/80 rounded-t" style={{ height: `${(d.people / maxPeople) * 88}%`, minHeight: 2 }} />
                </div>
              ))}
            </div>
            {!(data.by_day || []).length && <div className={`${T.body} text-gray-500 py-6 text-center`}>No bookings in this range</div>}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-200"><span className={`${T.label} text-gray-500`}>By desk</span></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>{['Desk', 'Office', 'Status', 'Bookings', 'Days used'].map(h => (
                    <th key={h} className={`${T.label} text-gray-500 px-3 py-2 text-left`}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(data.by_desk || []).map(d => (
                    <tr key={d.id} className={d.bookings ? '' : 'bg-amber-50/40'}>
                      <td className={`${T.body} font-semibold text-gray-800 px-3 py-2`}>{d.label}</td>
                      <td className={`${T.body} text-gray-600 px-3 py-2`}>{d.location_name}</td>
                      <td className={`${T.body} text-gray-600 px-3 py-2`}>{d.status}</td>
                      <td className={`${T.body} text-gray-700 px-3 py-2 tabular-nums`}>{d.bookings}</td>
                      <td className={`${T.body} text-gray-700 px-3 py-2 tabular-nums`}>{d.days_used}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {(data.by_whereabouts || []).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <div className={`${T.label} text-gray-500 mb-2`}>Stated whereabouts (person-days)</div>
              <div className="flex flex-wrap gap-2">
                {data.by_whereabouts.map(s => (
                  <span key={s.status_code} className={`${T.body} bg-gray-100 border border-gray-200 rounded-full px-3 py-1 text-gray-700`}>
                    {s.status_code}: <span className="font-semibold">{s.days}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'report',   label: 'Utilisation' },
  { id: 'desks',    label: 'Desks' },
  { id: 'policy',   label: 'Office rules' },
  { id: 'vans',     label: 'Vehicles' },
  { id: 'display',  label: 'Entrance display' },
];

export default function ResourceManagementPage({ user }) {
  const [tab, setTab] = useState('overview');
  return (
    <div className="fade-in">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className={`${T.page} text-navy`}>Desk &amp; Vehicle Management</h2>
          <p className={`${T.meta} text-gray-500 mt-0.5`}>
            See who is booked, how much of the office is actually used, and manage offices, desks, rules and vehicles.
          </p>
        </div>
      </div>
      <div className="flex gap-1 mb-4 border-b border-gray-200 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-2 ${T.body} font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-navy text-navy' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>{t.label}</button>
        ))}
      </div>
      {tab === 'overview' && <OverviewPanel />}
      {tab === 'report'   && <ReportPanel />}
      {tab === 'desks'    && <DesksPanel />}
      {tab === 'policy'   && <PolicyPanel />}
      {tab === 'vans'     && <VansPanel />}
      {tab === 'display'  && <DisplayDevicesPanel />}
    </div>
  );
}
